/**
 * additional-views.ts — kontrakt widoków dodatkowych (T22.0, api ↔ admin SPA).
 *
 * Widok dodatkowy = zapytanie SQL zdefiniowane przez admina, dopięte pod kartotekę (system lub
 * custom) i renderowane jako zakładka/panel na rekordzie. Lustro tabeli `ext_additional_views`
 * (`apps/api/drizzle/tenant/schema.ts`) — daty jako ISO string po serializacji JSON.
 *
 * `DictionaryParamBinding` / `DictionaryParamBindings` / `DictionarySourceParam` poniżej NIE są
 * importowane z `apps/api/lib/dictionaries/external-source-types.ts` — ten plik żyje w backendzie
 * (Node-only, importowany też przez lib/permissions.ts) i `packages/shared` nie powinien po niego
 * sięgać (paczka jest bundlowana też przez Vite dla SPA; kierunek zależności ma iść shared → api,
 * nigdy odwrotnie). Kształty poniżej są CELOWYM lustrem tamtych typów — zmiana etapowego modelu
 * parametrów (deklaracja/wiązanie) musi być wprowadzona w OBU miejscach.
 */

/** Typ zadeklarowanego parametru SQL — mirror `DictionaryParamType` (external-source-types.ts). */
export type AdditionalViewParamType = 'text' | 'int' | 'decimal' | 'date' | 'bool';

/**
 * Deklaracja parametru na widoku (etap 1) — mirror `DictionarySourceParam`
 * (external-source-types.ts).
 */
export interface AdditionalViewSourceParam {
  name: string;
  type: AdditionalViewParamType;
  required: boolean;
}

/**
 * Wiązanie zadeklarowanego parametru do źródła wartości (etap 2) — mirror
 * `DictionaryParamBinding` (external-source-types.ts).
 */
export type AdditionalViewParamBinding =
  | { kind: 'record_field'; fieldSlug: string }
  | { kind: 'context.company' }
  | { kind: 'context.user' }
  | { kind: 'constant'; value: string | number | boolean };

/** Mapa nazwa parametru → wiązanie — mirror `DictionaryParamBindings` (external-source-types.ts). */
export type AdditionalViewParamBindings = Record<string, AdditionalViewParamBinding>;

/**
 * Wiersz katalogu widoku dodatkowego, jaki API zwraca do SPA. Lustro tabeli
 * `ext_additional_views`.
 */
export interface AdditionalViewDefinition {
  id: string;
  slug: string;
  parentTableName: string;
  parentEntityType: 'system' | 'custom';
  parentEntity: string;
  displayName: string;
  displayNamePlural: string | null;
  icon: string | null;
  description: string | null;
  sourceSql: string;
  declaredParams: AdditionalViewSourceParam[];
  paramBindings: AdditionalViewParamBindings;
  statementTimeoutMs: number;
  isActive: boolean;
  sortOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Jedna kolumna wyniku zapytania widoku. `displayType` = klasyfikacja, po której SPA dobiera
 * renderer komórki; backend mapuje Postgres OID (`dataTypeID`) → `displayType` przy formatowaniu
 * wyniku.
 */
export interface AdditionalViewColumn {
  name: string;
  dataTypeID: number;
  displayType: 'text' | 'number' | 'amount' | 'date' | 'datetime' | 'bool';
}

/**
 * Odpowiedź endpointu kolumn widoku (`GET /api/settings/additional-views/:viewId/columns`):
 * kolumny wyniku zapytania w kolejności zapytania, bez wierszy (kreator formatek potrzebuje
 * samego układu kolumn, zanim istnieje rekord; karta rekordu bierze kolumny z trasy danych).
 */
export interface AdditionalViewColumnsResponse {
  columns: AdditionalViewColumn[];
}

/**
 * Stabilne, UNIKALNE identyfikatory kolumn widoku do układu kolumn (`settings.tableColumns` /
 * `hiddenColumnIds`). Zapytanie SQL może zwrócić kilka kolumn o tej samej nazwie (np. dwa razy
 * `kwota` z dwóch złączonych tabel), a układ wymaga jednoznacznego id — pierwsze wystąpienie
 * nazwy dostaje samą nazwę, kolejne `nazwa#2`, `nazwa#3`…
 *
 * Wygenerowany id nigdy nie koliduje z prawdziwą nazwą kolumny ani z już przydzielonym id
 * (np. kolumny `x`, `x`, `x#2` → `x`, `x#3`, `x#2` — drugi `x` nie może zająć `x#2`, bo to
 * nazwa trzeciej kolumny). Wynik jest deterministyczny i zależy wyłącznie od kolejności kolumn,
 * więc builder i karta rekordu liczą te same id z tej samej listy kolumn. Zwraca tablicę
 * równoległą do wejścia.
 */
export function additionalViewColumnIds(columns: ReadonlyArray<{ name: string }>): string[] {
  // Nazwy rzeczywiste są zarezerwowane z góry: wygenerowany sufiks `#n` nie może ich zająć.
  const realNames = new Set<string>(columns.map((column) => column.name));
  const assigned = new Set<string>();
  const nextSuffix = new Map<string, number>();
  const ids: string[] = [];
  for (const { name } of columns) {
    let id = name;
    if (assigned.has(id)) {
      let suffix = nextSuffix.get(name) ?? 2;
      id = `${name}#${suffix}`;
      while (assigned.has(id) || realNames.has(id)) {
        suffix += 1;
        id = `${name}#${suffix}`;
      }
      nextSuffix.set(name, suffix + 1);
    }
    assigned.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Wynik wykonania zapytania widoku. `rows` są tablicami POZYCYJNYMI wyrównanymi do `columns` po
 * indeksie (CELOWO nie obiekty keyed po nazwie — mniejszy payload, kolejność kolumn niesie
 * znaczenie). `truncated` = wynik przycięty do limitu wierszy po stronie API.
 */
export interface AdditionalViewData {
  columns: AdditionalViewColumn[];
  rows: unknown[][];
  truncated: boolean;
}

/**
 * Body żądania wykonania widoku dodatkowego (endpoint danych,
 * `POST /api/settings/additional-views/:viewId/data`). Wiązania parametrów żyją w zapisanej
 * definicji (`AdditionalViewDefinition.paramBindings`) — to body niesie WYŁĄCZNIE wartości pól
 * rekordu-hosta.
 *
 * GRANICA ZAUFANIA (T22 J1, opus review finding — HIGH): `recordId` i `companyId` CELOWO NIE SĄ
 * polami tego kształtu. Oba są kontekstem rezerwowym silnika (`reserved-params.ts`) i MUSZĄ
 * pochodzić z zaufanego stanu serwera, nigdy z ciała żądania:
 *  - `recordId`  ← query param `?recordId=<uuid>`, autoryzowany PRZED wykonaniem przez
 *    `canAccessAdditionalParent` (uprawnienie widoku RODZICA — zamyka record-level IDOR).
 *  - `companyId` ← nagłówek `X-Company-Id` zwalidowany przeciw `user_company_access`
 *    (`requireCompany`, ten sam helper co `/api/dictionaries/[code]/options`) — zamyka
 *    cross-company read. Wcześniejsza wersja tego kształtu niosła oba pola jako opcjonalne
 *    body-hinty — to był wektor ataku (klient mógł podać `recordId`/`companyId` DOWOLNEGO innego
 *    rekordu/firmy), usunięte w tym samym joinie co wdrożenie helpera autoryzującego.
 */
export interface ExecuteAdditionalViewDataInput {
  recordValues: Record<string, string | number | boolean | null>;
}
